import { Component, computed, effect, inject, signal, WritableSignal } from '@angular/core';
import { MatFormField, MatOption, MatSelect } from '@angular/material/select';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { DividerComponent } from '@r1-compass-library/components';
import { Play } from '@r1-compass-library/models';
import { PlayService } from '@r1-compass-library/services';
import { DateTime } from 'luxon';
import { StackedDrilldownChartComponent } from '../../stacked-drilldown-chart/stacked-drilldown-chart.component';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReportTableComponent } from '@r1-compass-library/components/reporting/report-table/report-table.component';
import { filterNil } from 'ngxtension/filter-nil';
import { BehaviorSubject } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { Environment } from '@r1-compass-library/environments/environment';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { ActionActivityDialogService, ActivityChartData } from './action-activity-dialog.service';
import { PlayExecutionTableRecord } from '@r1-compass-library/components/reporting/report-table/play-execution-table-record.model';
import { PlaySupportDetailsService } from '@r1-compass-library/services/play-support-details.service';

export type ActionActivityDialogData = {
    selectedDivisionIds: number[];
};
@Component({
    selector: 'r1-compass-app-action-activity-dialog',
    imports: [
        DividerComponent,
        FontAwesomeModule,
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatFormField,
        MatSelect,
        MatOption,
        StackedDrilldownChartComponent,
        CommonModule,
        ReportTableComponent,
    ],
    templateUrl: './action-activity-dialog.component.html',
    styleUrl: './action-activity-dialog.component.scss',
})
export class ActionActivityDialogComponent {
    readonly #library = inject(FaIconLibrary);
    readonly #playService = inject(PlayService);
    readonly #actionActivityDialogDataService = inject(ActionActivityDialogService);
    readonly #playSupportDetailsService = inject(PlaySupportDetailsService);

    public data = inject<ActionActivityDialogData>(MAT_DIALOG_DATA);

    private dateTimeNow = DateTime.now();

    dateFilters: Map<string, DateTime> = new Map([
        ['Last 30 minutes', this.dateTimeNow.minus({ minutes: 30 })],
        ['Last 1 hour', this.dateTimeNow.minus({ hours: 1 })],
        ['Last 2 hours', this.dateTimeNow.minus({ hours: 2 })],
        ['Last 4 hours', this.dateTimeNow.minus({ hours: 4 })],
        ['Last 8 hours', this.dateTimeNow.minus({ hours: 8 })],
        ['Last 24 hours', this.dateTimeNow.minus({ days: 1 })],
        ['Last 48 hours', this.dateTimeNow.minus({ days: 2 })],
        ['Last 7 days', this.dateTimeNow.minus({ days: 7 })],
        ['Last 30 days', this.dateTimeNow.minus({ days: 30 })],
        ['Last 3 months', this.dateTimeNow.minus({ months: 3 })],
    ]);
    appSpecificColumns = this.#actionActivityDialogDataService.appSpecificColumns;
    appSpecificDetails = this.#actionActivityDialogDataService.appSpecificDetails;

    searchStartDate: DateTime = this.dateTimeNow.minus({ days: 1 });
    searchEndDate: DateTime = this.dateTimeNow;

    chartData: ActivityChartData[] = this.#actionActivityDialogDataService.chartData();

    reportData = this.#actionActivityDialogDataService.reportData;
    interactionErrorTableData = this.#actionActivityDialogDataService.interactionErrorTableData;

    selectedPlay: WritableSignal<Play | null> = signal(null);
    selectedPlayId = computed(() => this.selectedPlay()?.id || null);
    selectedPlayUrl = computed(() => {
        const selectedPlayId = this.selectedPlayId();
        if (selectedPlayId) {
            return Environment.baseUrl + '/plays/' + selectedPlayId + '/definition';
        } else {
            return null;
        }
    });

    yesterday = this.dateTimeNow.minus({ days: 1 }).toISO() ?? '';

    selectedFilter$ = new BehaviorSubject<string | null>(this.yesterday);

    private readonly selectedFilter = toSignal(this.selectedFilter$.pipe(filterNil()), {
        initialValue: this.yesterday,
    });

    constructor() {
        const library = this.#library;
        effect(
            () => {
                const filter = this.selectedFilter();
                if (!filter) {
                    return;
                }
                this.searchEndDate = DateTime.now();
                this.searchStartDate = DateTime.fromISO(filter);
            },
            { debugName: 'date filter change' }
        );
        effect(
            () => {
                const filter = this.selectedFilter();
                if (!filter) {
                    return;
                }
                this.#actionActivityDialogDataService
                    .getChartData(filter, this.data.selectedDivisionIds)
                    .subscribe(aggregates => {
                        this.chartData.find(datum => datum.name === 'Created')!.values = aggregates[0];
                        this.chartData.find(datum => datum.name === 'Updated')!.values = aggregates[1];
                        this.chartData.find(datum => datum.name === 'Failed')!.values = aggregates[2];
                        this.chartData.find(datum => datum.name === 'Rejected')!.values = aggregates[3];
                        this.chartData = [...this.chartData];
                    });
            },
            { debugName: 'getting aggregate records' }
        );
        library.addIcons(faTimes);
    }

    dateRangeChange(range: string) {
        this.selectedPlay.set(null);
        this.#actionActivityDialogDataService.reset();
        this.selectedFilter$.next(range);
    }

    getInteractionErrorTableData(row: PlayExecutionTableRecord) {
        this.#actionActivityDialogDataService.getInteractionErrorTableData(row.playResultId);
    }

    downloadSupportData(row: PlayExecutionTableRecord) {
        const playId = this.selectedPlayId();
        if (playId) {
            this.#playSupportDetailsService.downloadSupportData(playId, row.startDate, row.executionId);
        }
    }

    loadPlayReportingDetails(event: RowSelectedEvent) {
        const playId = event.series;
        if (playId != this.selectedPlayId()) {
            this.selectedPlay.set(null);
            this.#actionActivityDialogDataService.getReportingTableData(
                playId,
                this.searchStartDate.toISO()!,
                this.searchEndDate.toISO()!
            );
            this.#playService.get(playId).subscribe(play => this.selectedPlay.set(play));
        }
    }
}

export type RowSelectedEvent = {
    name: string;
    value: number;
    label: string;
    series: number;
};
